import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChatListItem, ChatMessage, ChatMessageImage, ChatMilestone, VerboseLogEntry } from '../models/chat';
import {
  createChat as apiCreateChat,
  getChatMessages,
  getChatMilestones,
  latestChat,
  listProjectChats,
  sendChatMessage,
} from '../services/chats';
import { chatFailureMessage } from '../utils/actionFailure';

const SYNTHETIC_USER_ID_PREFIX = 'optimistic-';

async function readMilestones(chatId: string): Promise<ChatMilestone[] | null> {
  try {
    return await getChatMilestones(chatId);
  } catch (err) {
    console.warn('Failed to read the steps of chat:', chatId, err);
    return null;
  }
}

/** Steps pushed while a read was on its way are kept; a step both read and pushed is kept once, as read. */
function withMilestones(shown: ChatMilestone[] | undefined, added: ChatMilestone[]): ChatMilestone[] {
  const byId = new Map((shown ?? []).map((step) => [step.id.toLowerCase(), step]));
  for (const step of added) byId.set(step.id.toLowerCase(), step);
  return [...byId.values()].sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt));
}

function withReadMilestones(
  byChat: Record<string, ChatMilestone[]>,
  reads: ReadonlyArray<readonly [string, ChatMilestone[] | null]>,
): Record<string, ChatMilestone[]> {
  const next = { ...byChat };
  for (const [chatId, read] of reads) {
    if (read) next[chatId] = withMilestones(next[chatId], read);
  }
  return next;
}

function chatsWithNewActivity(before: ChatListItem[], after: ChatListItem[]): string[] {
  const now = new Map(after.map((chat) => [chat.id, chat]));
  return before
    .filter((chat) => {
      const read = now.get(chat.id);
      return read !== undefined && (read.currentJobId !== chat.currentJobId || read.updatedAt !== chat.updatedAt);
    })
    .map((chat) => chat.id);
}

function onlyChats<T>(byChat: Record<string, T>, chatIds: Set<string>): Record<string, T> {
  return Object.fromEntries(Object.entries(byChat).filter(([chatId]) => chatIds.has(chatId)));
}

function sameChats(shown: ChatListItem[], read: ChatListItem[]): boolean {
  if (shown.length !== read.length) return false;
  const shownById = new Map(shown.map((chat) => [chat.id, chat]));
  return read.every((chat) => {
    const known = shownById.get(chat.id);
    return (
      known !== undefined &&
      known.currentJobId === chat.currentJobId &&
      known.lastVersionId === chat.lastVersionId &&
      known.updatedAt === chat.updatedAt &&
      known.name === chat.name
    );
  });
}

function makeOptimisticUserMessage(chatId: string, content: string, images?: ChatMessageImage[]): ChatMessage {
  return {
    id: `${SYNTHETIC_USER_ID_PREFIX}${Date.now()}`,
    chatId,
    role: 'User',
    content,
    jobId: null,
    state: 'None',
    createdAt: new Date().toISOString(),
    images: images && images.length > 0 ? images : undefined,
  };
}

export interface ResyncOptions {
  /** A chat list read just now, used instead of reading it again. */
  list?: ChatListItem[];
  /** Runs after the reads; the chats change on screen together with what it refreshes. */
  refreshAlongside?: () => Promise<unknown>;
}

export interface UseChatsResult {
  chats: ChatListItem[];
  currentChatId: string | null;
  isDraftMode: boolean;
  messagesByChat: Record<string, ChatMessage[]>;
  milestonesByChat: Record<string, ChatMilestone[]>;
  verboseLogsByChat: Record<string, VerboseLogEntry[]>;
  isLoadingChats: boolean;
  isLoadingMessages: boolean;
  sendError: string | null;

  currentChat: ChatListItem | null;
  currentMessages: ChatMessage[];
  isCurrentRunning: boolean;
  isAnyRunning: boolean;
  /** Every step the open chat's jobs reported, oldest first. */
  currentMilestones: ChatMilestone[];
  currentVerboseLogs: VerboseLogEntry[];

  selectChat: (chatId: string) => void;
  beginDraftChat: () => void;
  /** Resolves true once the server accepted the message; otherwise sets `sendError` and resolves false. */
  sendMessage: (content: string, attachments?: ChatMessageImage[]) => Promise<boolean>;
  /** Resolves true once the server started the chat; otherwise sets `sendError` and resolves false. */
  startChat: (firstMessage: string) => Promise<boolean>;

  /**
   * Reads the chat list alone and returns it when it differs from the chats on screen (a chat added or removed, or
   * a chat's job, last version, last activity or name). Null when nothing changed, when the read failed, or when a
   * message was sent or a re-sync started while it was reading.
   */
  readChangedChats: () => Promise<ChatListItem[] | null>;

  /**
   * Re-reads from the API the chat list and the messages and steps of the open chat and of every chat already loaded
   * in this tab whose job or last activity changed, the steps of every loaded chat still running, and clears the logs
   * of chats no longer running. A chat not loaded yet is read when it is opened. A read overtaken by a newer read or by
   * a message sent in the meantime is dropped.
   */
  resync: (options?: ResyncOptions) => Promise<void>;

  handleMilestone: (milestone: ChatMilestone) => void;
  handleVerboseLog: (chatId: string, level: string, message: string, ts: string) => void;
  handleSystemMessage: (message: ChatMessage) => void;
}

export function useChats(projectId: string): UseChatsResult {
  const [chats, setChats] = useState<ChatListItem[]>([]);
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const [isDraftMode, setIsDraftMode] = useState(false);
  const [messagesByChat, setMessagesByChat] = useState<Record<string, ChatMessage[]>>({});
  const [milestonesByChat, setMilestonesByChat] = useState<Record<string, ChatMilestone[]>>({});
  const [verboseLogsByChat, setVerboseLogsByChat] = useState<Record<string, VerboseLogEntry[]>>({});
  const [isLoadingChats, setIsLoadingChats] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const projectIdRef = useRef(projectId);
  useEffect(() => {
    projectIdRef.current = projectId;
  }, [projectId]);

  const chatsRef = useRef(chats);
  const currentChatIdRef = useRef(currentChatId);
  const messagesByChatRef = useRef(messagesByChat);
  useEffect(() => {
    chatsRef.current = chats;
    currentChatIdRef.current = currentChatId;
    messagesByChatRef.current = messagesByChat;
  });

  const readGenerationRef = useRef(0);

  // Initial load: chats list, then most-recent chat's messages.
  useEffect(() => {
    if (!projectId) return;

    let cancelled = false;
    (async () => {
      setIsLoadingChats(true);
      try {
        const list = await listProjectChats(projectId);
        if (cancelled) return;

        setChats(list);
        const initial = latestChat(list);
        if (initial) {
          setCurrentChatId(initial.id);

          setIsLoadingMessages(true);
          try {
            const [msgs, steps] = await Promise.all([getChatMessages(initial.id), readMilestones(initial.id)]);
            if (!cancelled) {
              setMessagesByChat((prev) => ({ ...prev, [initial.id]: msgs }));
              setMilestonesByChat((prev) => withReadMilestones(prev, [[initial.id, steps]]));
            }
          } finally {
            if (!cancelled) setIsLoadingMessages(false);
          }
        } else {
          setIsDraftMode(true);
        }
      } catch (err) {
        console.error('Failed to load chats:', err);
      } finally {
        if (!cancelled) setIsLoadingChats(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const selectChat = useCallback(async (chatId: string) => {
    setSendError(null);
    setIsDraftMode(false);
    setCurrentChatId(chatId);
    if (!messagesByChat[chatId]) {
      setIsLoadingMessages(true);
      try {
        const [msgs, steps] = await Promise.all([getChatMessages(chatId), readMilestones(chatId)]);
        setMessagesByChat((prev) => ({ ...prev, [chatId]: msgs }));
        setMilestonesByChat((prev) => withReadMilestones(prev, [[chatId, steps]]));
      } catch (err) {
        console.error('Failed to load messages for chat:', chatId, err);
      } finally {
        setIsLoadingMessages(false);
      }
    }
  }, [messagesByChat]);

  const beginDraftChat = useCallback(() => {
    setSendError(null);
    setIsDraftMode(true);
    setCurrentChatId(null);
  }, []);

  const createChatWith = useCallback(async (firstMessage: string, imageIds?: string[]) => {
    setSendError(null);
    try {
      const result = await apiCreateChat(projectIdRef.current, firstMessage, undefined, imageIds);
      readGenerationRef.current += 1;
      setChats((prev) => [result.chat, ...prev.filter((c) => c.id !== result.chat.id)]);
      setMessagesByChat((prev) => ({ ...prev, [result.chat.id]: result.messages }));
      setCurrentChatId(result.chat.id);
      setIsDraftMode(false);
      return true;
    } catch (err) {
      console.error('Failed to create chat:', err);
      setSendError(chatFailureMessage(err, 'Failed to start a new chat. Please try again.'));
      return false;
    }
  }, []);

  const startChat = useCallback((firstMessage: string) => createChatWith(firstMessage), [createChatWith]);

  const sendMessage = useCallback(async (content: string, attachments?: ChatMessageImage[]) => {
    setSendError(null);
    const trimmed = content.trim();
    if (!trimmed) return false;

    const imageIds = attachments?.map((a) => a.id);

    if (isDraftMode) return createChatWith(trimmed, imageIds);

    const chatId = currentChatId;
    if (!chatId) return false;

    const optimistic = makeOptimisticUserMessage(chatId, trimmed, attachments);
    readGenerationRef.current += 1;
    setMessagesByChat((prev) => ({
      ...prev,
      [chatId]: [...(prev[chatId] ?? []), optimistic],
    }));

    try {
      const { jobId } = await sendChatMessage(chatId, trimmed, imageIds);
      readGenerationRef.current += 1;
      setChats((prev) =>
        prev.map((c) =>
          c.id === chatId ? { ...c, currentJobId: jobId, updatedAt: optimistic.createdAt } : c,
        ),
      );
      return true;
    } catch (err) {
      console.error('Failed to send message:', err);
      setMessagesByChat((prev) => ({
        ...prev,
        [chatId]: (prev[chatId] ?? []).filter((m) => m.id !== optimistic.id),
      }));
      setSendError(chatFailureMessage(err, 'Failed to send message. Please try again.'));
      return false;
    }
  }, [currentChatId, isDraftMode, createChatWith]);

  const handleMilestone = useCallback((milestone: ChatMilestone) => {
    setMilestonesByChat((prev) => ({
      ...prev,
      [milestone.chatId]: withMilestones(prev[milestone.chatId], [milestone]),
    }));
  }, []);

  const handleVerboseLog = useCallback((chatId: string, level: string, message: string, ts: string) => {
    setVerboseLogsByChat((prev) => ({
      ...prev,
      [chatId]: [...(prev[chatId] ?? []), { level, message, timestamp: ts }],
    }));
  }, []);

  const readChangedChats = useCallback(async () => {
    const generation = readGenerationRef.current;
    try {
      const list = await listProjectChats(projectIdRef.current);
      if (generation !== readGenerationRef.current) return null;
      return sameChats(chatsRef.current, list) ? null : list;
    } catch (err) {
      console.warn('Failed to read chats:', err);
      return null;
    }
  }, []);

  const resync = useCallback(async ({ list: knownList, refreshAlongside }: ResyncOptions = {}) => {
    const generation = ++readGenerationRef.current;
    try {
      const list = knownList ?? (await listProjectChats(projectIdRef.current));
      const loaded = messagesByChatRef.current;
      const reread = new Set(chatsWithNewActivity(chatsRef.current, list).filter((chatId) => chatId in loaded));
      if (currentChatIdRef.current) reread.add(currentChatIdRef.current);
      const running = new Set(list.filter((c) => c.currentJobId !== null).map((c) => c.id));
      const stepsReread = new Set([...reread, ...[...running].filter((chatId) => chatId in loaded)]);
      const [messages, steps] = await Promise.all([
        Promise.all([...reread].map(async (chatId) => [chatId, await getChatMessages(chatId)] as const)),
        Promise.all([...stepsReread].map(async (chatId) => [chatId, await readMilestones(chatId)] as const)),
      ]);
      await refreshAlongside?.();
      if (generation !== readGenerationRef.current) return;

      setChats(list);
      setMessagesByChat((prev) => ({ ...prev, ...Object.fromEntries(messages) }));
      setMilestonesByChat((prev) => withReadMilestones(prev, steps));
      setVerboseLogsByChat((logs) => onlyChats(logs, running));
    } catch (err) {
      console.error('Failed to re-read chats:', err);
    }
  }, []);

  // A chat whose messages were never loaded stays unloaded, so opening it later fetches all of them.
  const handleSystemMessage = useCallback((message: ChatMessage) => {
    setMessagesByChat((prev) => {
      const loaded = prev[message.chatId];
      if (!loaded || loaded.some((m) => m.id === message.id)) return prev;
      return { ...prev, [message.chatId]: [...loaded, message] };
    });
  }, []);

  const currentChat = useMemo(
    () => chats.find((c) => c.id === currentChatId) ?? null,
    [chats, currentChatId],
  );

  const currentMessages = useMemo(
    () => (currentChatId ? messagesByChat[currentChatId] ?? [] : []),
    [currentChatId, messagesByChat],
  );

  const isCurrentRunning = useMemo(
    () => !!currentChat && currentChat.currentJobId !== null,
    [currentChat],
  );

  const isAnyRunning = useMemo(
    () => chats.some((c) => c.currentJobId !== null),
    [chats],
  );

  const currentMilestones = useMemo(
    () => (currentChatId ? milestonesByChat[currentChatId] ?? [] : []),
    [currentChatId, milestonesByChat],
  );

  const currentVerboseLogs = useMemo(
    () => (currentChatId ? verboseLogsByChat[currentChatId] ?? [] : []),
    [currentChatId, verboseLogsByChat],
  );

  return {
    chats,
    currentChatId,
    isDraftMode,
    messagesByChat,
    milestonesByChat,
    verboseLogsByChat,
    isLoadingChats,
    isLoadingMessages,
    sendError,

    currentChat,
    currentMessages,
    isCurrentRunning,
    isAnyRunning,
    currentMilestones,
    currentVerboseLogs,

    selectChat,
    beginDraftChat,
    sendMessage,
    startChat,

    readChangedChats,
    resync,

    handleMilestone,
    handleVerboseLog,
    handleSystemMessage,
  };
}
